import { LogsContainer, LogsList } from './styles';
import { useGetVersionsLazyQuery, Version } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { titleCaseSentence } from '@/utils';
import { PoiSuspenseFallback } from '../PoiSuspenseFallback';
import { LogItem } from './LogItem';
import { Typography } from '@mui/material';

type LogsProps = {
  collectionName: string;
  referenceId?: string;
  poiType?: string;
  isLogsTab?: boolean;
};

type FileInfo = {
  fileName: string;
  fileSize: number;
};

type UpdatedValue = string | FileInfo[] | { templateFieldName: string; value: string }[] | string[];

type UpdatedData = {
  [key: string]: UpdatedValue;
};

export type LogEntryType = {
  id: string;
  user: {
    name: string;
    avatar?: string;
    initials: string | null;
  };
  action: string;
  timestamp: string;
  details: { key: string; to: string }[];
};

export const Logs = ({ collectionName, referenceId, poiType, isLogsTab }: LogsProps) => {
  const structureId = useGetStructureId();
  const poiTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const [logEntries, setLogEntries] = useState<LogEntryType[]>([]);
  const [getVersions, { data, loading }] = useGetVersionsLazyQuery({
    variables: {
      input: {
        structureId,
        collectionName,
        referenceId,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const findTemplate = useMemo(() => {
    if (!poiType) return undefined;
    const template = poiTemplates.find(
      (template) => template.name.toLocaleLowerCase() === poiType.toLocaleLowerCase()
    );
    return template;
  }, [poiType, poiTemplates]);

  const templateFields = useMemo(() => {
    if (!findTemplate?.fields) {
      return [];
    }
    return findTemplate?.fields?.filter((field) => !field.isHidden);
  }, [findTemplate?.fields]);

  const updateTemplateFieldData = useCallback(
    (data: UpdatedValue | null) => {
      if (!data) return;
      if (Array.isArray(data)) {
        const updated = data
          .map((item) => {
            if (typeof item !== 'object' || !('_id' in item)) return null;

            const templateFieldName = templateFields.find((field) => field.id === item._id)?.name;

            if (!templateFieldName) return null;

            return {
              ...item,
              value: 'value' in item ? item.value : '',
              templateFieldName,
            };
          })
          .filter((item) => item !== null);

        return updated;
      }
    },
    [templateFields]
  );

  const prettifyKey = useCallback((text: string): string => {
    const result = text
      .replace(/\./g, ' ') // Replace dots with spaces
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle ALLCAPS followed by Camel
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .replace(/files/gi, 'file') // Replace files with file
      .trim();

    return result.charAt(0).toUpperCase() + result.slice(1);
  }, []);

  const formatValue = (value: string | number | UpdatedValue | null): string =>
    typeof value === 'number' ? Number(value).toFixed(4) : String(value);

  const buildToString = useCallback(
    (key: string, previousValue: UpdatedValue | null, formattedValue: string | number): string => {
      const prefix = previousValue ? `${formatValue(previousValue)} > ` : `${prettifyKey(key)} > `;
      return `${prefix}${formatValue(formattedValue)}`;
    },
    [prettifyKey]
  );

  const getDetailsFromUpdateData = useCallback(
    (version: Version) => {
      if (!version.updatedData || typeof version.updatedData !== 'object') return undefined;
      const updatedData: UpdatedData = version.updatedData;
      const previousData: UpdatedData = version.previousData;

      const isEmptyValue = (value: unknown): boolean => {
        return (
          value === null ||
          value === undefined ||
          value === false ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        );
      };

      // if updatedData is an array, we need to handle it differently
      const meaningfulEntries = Object.entries(updatedData)
        .filter(([, value]) => !isEmptyValue(value))
        .map(([key, value]) => {
          let formattedValue = value;

          let previousValue = null;
          if (previousData) previousValue = previousData[key];
          // ignore following keys
          if (key.includes('uploadStatus')) return [];
          if (key === 'screenshotPaths') return [];
          if (key === 'centerPoint3d') return [];
          if (!key || !formattedValue) return [];

          // we will show empty entry for cameraLocation
          if (key === 'cameraLocation') {
            return {
              key: 'POI Location',
              to: '',
            };
          }

          // we will show empty entry for workpackId and paintBlockId
          // TODO: find a way to get the workpack, paint block, and paint region name from the id without making a new request
          if (key === 'workpackId' || key === 'paintBlockId' || key === 'paintRegionId') {
            return {
              key: prettifyKey(key),
              to: '',
            };
          }

          // template data handling
          if (key === 'templateData') {
            const transformedTemplateFieldData = updateTemplateFieldData(formattedValue);
            const transformedPreviousValue = updateTemplateFieldData(previousValue);
            const tempObj = transformedTemplateFieldData
              ?.map((item) => {
                if (!item.templateFieldName) return [];
                const previousValue = transformedPreviousValue?.find(
                  (prev) => prev.templateFieldName === item.templateFieldName
                );
                if (previousValue?.value === item.value) {
                  return null;
                }
                return {
                  key: item.templateFieldName,
                  to: `${
                    previousValue?.value
                      ? previousValue?.value + ' > '
                      : titleCaseSentence(item.templateFieldName ?? 'N/A') + ' > '
                  }${'value' in item && item.value !== '' ? item.value : 'empty'}`,
                };
              })
              .filter(Boolean);
            return tempObj;
          }

          // file uploads handling
          if (key.includes('files')) {
            if (Array.isArray(formattedValue)) {
              formattedValue = formattedValue.map((file) => ({
                fileName: typeof file === 'object' && 'fileName' in file ? file.fileName : '',
                fileSize: typeof file === 'object' && 'fileSize' in file ? file.fileSize : 0,
              }));
              const fileName = Array.isArray(formattedValue)
                ? formattedValue.map((file) => file.fileName).join(', ')
                : '';

              return {
                key: prettifyKey(key),
                to: `${prettifyKey(key)} uploaded ${fileName}`,
              };
            }
            if (typeof formattedValue === 'object') {
              const fileName = (formattedValue as FileInfo)?.fileName ?? '';
              return {
                key: prettifyKey(key),
                to: `${prettifyKey(key)} uploaded ${fileName} `,
              };
            }
          }

          // date handling
          if (key.toLowerCase().includes('date')) {
            if (Array.isArray(formattedValue)) {
              formattedValue = formattedValue.map((date) => {
                const parsedDate =
                  typeof date === 'string' || typeof date === 'number' ? new Date(date) : null;
                return parsedDate ? parsedDate.toLocaleDateString() : '';
              });
              if (previousValue && Array.isArray(previousValue)) {
                previousValue = previousValue.map((date) => {
                  const parsedDate =
                    typeof date === 'string' || typeof date === 'number' ? new Date(date) : null;
                  return parsedDate ? parsedDate.toLocaleDateString() : '';
                });
              }
            } else {
              const parsedDate = new Date(formattedValue);
              formattedValue = parsedDate ? parsedDate.toLocaleDateString() : '';
              if (
                previousValue &&
                (typeof previousValue === 'string' || typeof previousValue === 'number')
              ) {
                const parsedPreviousDate = new Date(previousValue);
                previousValue = parsedPreviousDate ? parsedPreviousDate.toLocaleDateString() : '';
              }
            }
            return {
              key: prettifyKey(key),
              to: `${
                previousValue ? previousValue + ' > ' : prettifyKey(key) + ' > '
              }${formattedValue}`,
            };
          }

          // object handling
          if (typeof formattedValue === 'object') {
            formattedValue = JSON.stringify(formattedValue);
          }

          return {
            key: prettifyKey(key),
            to: buildToString(key, previousValue, formattedValue),
          };
        });
      return meaningfulEntries.length > 0 ? meaningfulEntries : undefined;
    },
    [prettifyKey, buildToString, updateTemplateFieldData]
  );

  const transformVersionsToLogs = useCallback(
    (versions: Version[]) => {
      return versions.flatMap((version) => {
        if (version.lastModifiedBy === 'CLI' && version.lastModifiedFor === 'System Update')
          return [];

        const user = version.lastModifiedByUser;
        const initials =
          user?.name
            ?.split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase() ?? null;

        const timestamp = new Date(Number(version.createdAt));
        const formattedTimestamp = `${timestamp.getFullYear()}-${String(
          timestamp.getMonth() + 1
        ).padStart(2, '0')}-${String(timestamp.getDate()).padStart(2, '0')} ${String(
          timestamp.getHours()
        ).padStart(2, '0')}:${String(timestamp.getMinutes()).padStart(2, '0')}`;

        if (version.lastModifiedFor === 'Initialization') {
          return [
            {
              id: version.id,
              user: {
                name: user?.name ?? 'System',
                avatar: user?.picture ?? undefined,
                initials: initials,
              },
              action: 'created',
              timestamp: timestamp.toISOString(),
              details: [
                {
                  key: 'poi',
                  to: '',
                  timestamp: formattedTimestamp,
                },
              ],
            },
          ];
        }

        const details = getDetailsFromUpdateData(version);
        if (!details || details.length === 0) return [];

        const flatDetails = details.flat();
        return flatDetails.map((detail) => ({
          id: version.id,
          user: {
            name: user?.name ?? 'Unknown User',
            avatar: user?.picture ?? undefined,
            initials,
          },
          action: 'updated ',
          timestamp: timestamp.toISOString(),
          details: [detail],
        }));
      });
    },
    [getDetailsFromUpdateData]
  );

  const fetchLogs = useCallback(async () => {
    try {
      const res = await getVersions();
      const logs = res.data?.allVersions
        ? transformVersionsToLogs(
            [...res.data.allVersions].sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
          )
        : [];
      setLogEntries(logs as LogEntryType[]);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  }, [getVersions, transformVersionsToLogs]);

  useEffect(() => {
    if (isLogsTab) fetchLogs();
  }, [isLogsTab, fetchLogs]);

  return (
    <LogsContainer direction="column">
      {loading ? (
        <PoiSuspenseFallback />
      ) : (data?.allVersions && data.allVersions.length === 0) || logEntries.length === 0 ? (
        <LogsList>
          <Typography variant="body1">No logs available</Typography>
        </LogsList>
      ) : (
        <LogsList>
          {logEntries.map(
            (log, index) =>
              logEntries.length > 0 && (
                <LogItem
                  key={log.id}
                  log={log}
                  isFirst={index === 0}
                  isLast={index === logEntries.length - 1}
                />
              )
          )}
        </LogsList>
      )}
    </LogsContainer>
  );
};
